import { ApiError } from '@batiflow/client-core';
import { RightPanel, SkeletonList, useDrawerStyles } from '@batiflow/ui';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  OverlayDrawer,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { ShareLinksSection } from '@/features/share-links';
import { DiffusionSection } from '@/features/transmittals';
import { useStandalone } from '@/shared/components/standalone-frame';
import { ApprovalPanelBadge, ApprovalsSection } from './approvals/approvals-section';
import { DocumentDetails } from './document-details';
import { useDocument } from './documents-api';
import { usePanelStyles } from './panel-styles';
import { SignatureSection } from './signature-section';
import type { Uploads } from './use-uploads';
import { VersionHistory } from './version-history';

export interface DocumentPanelProps {
  /** Null in another organisation's space (no organisation-level reads). */
  orgId: string | null;
  /** The project of the space, when the reader can open it. */
  projectId: string | null;
  spaceId: string;
  /** The document to show; null keeps the panel closed. */
  documentId: string | null;
  /** The tab's uploads: a new version goes through the same controller as any file. */
  uploads: Uploads;
  /** The caller manages the space: "Send this document" and the share links are offered. */
  canTransmit?: boolean;
  onClose: () => void;
}

/**
 * The document right panel (docs/plan/13-ui-ux.md §1): opens from a row of the files list, Esc
 * closes it, it overlays the page under 1440 px. Details with an edit form, then the version
 * history with restore and "New version".
 */
export function DocumentPanel({
  orgId,
  projectId,
  spaceId,
  documentId,
  uploads,
  canTransmit = false,
  onClose,
}: DocumentPanelProps) {
  const { t } = useTranslation('documents');
  const drawerStyles = useDrawerStyles();
  const styles = usePanelStyles();
  const standalone = useStandalone();
  const document = useDocument(spaceId, documentId);
  const notFound = document.error instanceof ApiError && document.error.status === 404;

  let body;
  if (document.data) {
    body = (
      <div className={styles.root}>
        <ApprovalPanelBadge spaceId={spaceId} documentId={document.data.id} />
        <DocumentDetails
          orgId={orgId}
          projectId={projectId}
          spaceId={spaceId}
          document={document.data}
        />
        <ApprovalsSection orgId={orgId} document={document.data} />
        <SignatureSection orgId={orgId} document={document.data} />
        <DiffusionSection spaceId={spaceId} document={document.data} canTransmit={canTransmit} />
        <ShareLinksSection spaceId={spaceId} document={document.data} canShare={canTransmit} />
        <VersionHistory
          spaceId={spaceId}
          document={document.data}
          uploads={uploads}
          viewer={orgId !== null && projectId !== null ? { orgId, projectId } : null}
        />
      </div>
    );
  } else if (document.isError) {
    body = (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>
          <MessageBarTitle>
            {notFound ? t('panel.notFoundTitle') : t('panel.loadFailedTitle')}
          </MessageBarTitle>
          {notFound ? t('panel.notFoundBody') : t('panel.loadFailedBody')}
        </MessageBarBody>
        <MessageBarActions>
          {notFound ? (
            <Button onClick={onClose}>{t('panel.close')}</Button>
          ) : (
            <Button
              onClick={() => {
                void document.refetch();
              }}
            >
              {t('common:actions.retry')}
            </Button>
          )}
        </MessageBarActions>
      </MessageBar>
    );
  } else {
    body = <SkeletonList count={6} />;
  }

  const title = document.data?.title ?? t('panel.title');
  // Outside the organisation shell (a guest's space, COL-FE-03) there is no right panel: the same
  // content opens as a drawer over the page.
  if (standalone)
    return (
      <OverlayDrawer
        position="end"
        className={drawerStyles.root}
        open={documentId !== null}
        onOpenChange={(_event, data) => {
          if (!data.open) onClose();
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            heading={{ as: 'h2' }}
            action={
              <Button
                appearance="subtle"
                icon={<Dismiss20Regular />}
                aria-label={t('common:actions.close')}
                onClick={onClose}
              />
            }
          >
            {title}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>{documentId !== null ? body : null}</DrawerBody>
      </OverlayDrawer>
    );
  return (
    <RightPanel open={documentId !== null} title={title} onClose={onClose}>
      {documentId !== null ? body : null}
    </RightPanel>
  );
}
